import { useCallback, useEffect, useMemo, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { FinishScreen } from "@/components/FinishScreen";
import { buildShareText, loadBest, saveBest, loadBloomLevel, saveBloomLevel } from "@/lib/game-utils";
import { buildLevel, isWord, levelStars, wordScore, bonusScore, isFormable, TOTAL_BOARDS, type BloomLevel } from "@/data/word-bloom";

const STORAGE_KEY = "word-bloom";
const HINT_COST = 15;

export function WordBloom({ startLevel = 1 }: { startLevel?: number }) {
  const live = useLiveMode();
  const [level, setLevel] = useState(startLevel);
  const [board, setBoard] = useState<BloomLevel>(() => buildLevel(startLevel));
  const [letters, setLetters] = useState<string[]>(() => board.letters);
  const [picked, setPicked] = useState<number[]>([]);
  const [found, setFound] = useState<Set<string>>(() => new Set());
  const [revealed, setRevealed] = useState<Record<string, number>>({});
  const [hintsUsed, setHintsUsed] = useState(0);
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState(0);
  const [flash, setFlash] = useState<{ kind: "hit" | "miss" | "dupe"; text: string } | null>(null);
  const [levelDone, setLevelDone] = useState(false);
  const [finished, setFinished] = useState(false);
  const [best, setBest] = useState(0);
  // Bonus words found this level (real words that are not on the board).
  const [bonus, setBonus] = useState<Set<string>>(() => new Set());
  // The extended dictionary loads lazily so the 1 MB list never blocks first paint.
  const [dict, setDict] = useState<Set<string> | null>(null);

  useEffect(() => {
    let alive = true;
    import("@/data/word-bloom-extended").then((m) => {
      if (alive) setDict(m.EXTENDED_SET);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setBest(loadBest(STORAGE_KEY));
  }, []);

  const loadBoard = useCallback((n: number) => {
    const b = buildLevel(n);
    setBoard(b);
    setLetters(b.letters);
    setPicked([]);
    setFound(new Set());
    setRevealed({});
    setBonus(new Set());
    setHintsUsed(0);
    setStars(0);
    setFlash(null);
    setLevelDone(false);
    setLevel(n);
    saveBloomLevel(n);
  }, []);

  useEffect(() => {
    loadBoard(startLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLevel]);

  const current = picked.map((i) => letters[i]).join("");
  const remaining = board.answers.filter((w) => !found.has(w));

  const byLength = useMemo(() => {
    const groups = new Map<number, string[]>();
    for (const w of board.answers) {
      const list = groups.get(w.length) ?? [];
      list.push(w);
      groups.set(w.length, list);
    }
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [board]);

  useEffect(() => {
    if (!flash) return;
    const id = setTimeout(() => setFlash(null), 1100);
    return () => clearTimeout(id);
  }, [flash]);

  const submit = useCallback(() => {
    if (levelDone) return;
    const word = picked.map((i) => letters[i]).join("");
    setPicked([]);
    if (word.length < 3) {
      setFlash({ kind: "miss", text: "Words need at least 3 letters" });
      return;
    }
    if (found.has(word)) {
      setFlash({ kind: "dupe", text: `Already found ${word.toUpperCase()}` });
      return;
    }
    if (!board.answers.includes(word)) {
      // A real word that is not on this board still pays — a smaller bonus.
      const real = isWord(word) || (dict?.has(word) ?? false);
      if (real && isFormable(word, letters)) {
        const gained = bonusScore(word);
        setBonus((prev) => {
          const next = new Set(prev);
          next.add(word);
          return next;
        });
        setScore((s) => s + gained);
        setFlash({ kind: "hit", text: `Bonus ${word.toUpperCase()} +${gained}` });
        return;
      }
      setFlash({
        kind: "miss",
        text: real ? `${word.toUpperCase()} isn't on this board` : "Not a word",
      });
      return;
    }
    const gained = wordScore(word) + (word === board.pangram ? 50 : 0);
    const nextFound = new Set(found);
    nextFound.add(word);
    setFound(nextFound);
    setScore((s) => s + gained);
    setFlash({ kind: "hit", text: `${word.toUpperCase()} +${gained}` });

    if (nextFound.size === board.answers.length) {
      const earned = levelStars(hintsUsed);
      setStars(earned);
      setLevelDone(true);
      const total = score + gained;
      saveBest(STORAGE_KEY, total);
      setBest((b) => Math.max(b, total));
    }
  }, [board, dict, found, hintsUsed, letters, levelDone, picked, score]);

  const pressLetter = (idx: number) => {
    if (levelDone || picked.includes(idx)) return;
    setPicked((p) => [...p, idx]);
  };

  const typeLetter = (ch: string) => {
    const idx = letters.findIndex((l, i) => l === ch && !picked.includes(i));
    if (idx >= 0) setPicked((p) => [...p, idx]);
  };

  const shuffleWheel = () => {
    setPicked([]);
    setLetters((ls) => {
      const out = [...ls];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    });
  };

  const useHint = () => {
    if (levelDone || !remaining.length) return;
    // Reveal the next hidden letter of the shortest unfinished word.
    const target = [...remaining].sort(
      (a, b) => (revealed[a] ?? 0) - (revealed[b] ?? 0) || a.length - b.length,
    )[0];
    const shown = revealed[target] ?? 0;
    if (shown >= target.length) return;
    setRevealed((r) => ({ ...r, [target]: shown + 1 }));
    setHintsUsed((h) => h + 1);
    setScore((s) => Math.max(0, s - HINT_COST));
    setFlash({ kind: "dupe", text: `Hint: a ${target.length}-letter word starts with ${target.slice(0, shown + 1).toUpperCase()}` });
  };

  // Physical keyboard support.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (finished || levelDone) return;
      if (e.key === "Enter") { submit(); return; }
      if (e.key === "Backspace") { setPicked((p) => p.slice(0, -1)); return; }
      if (e.key === "Escape") { setPicked([]); return; }
      if (/^[a-zA-Z]$/.test(e.key)) typeLetter(e.key.toLowerCase());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const nextLevel = () => loadBoard(level + 1);

  const cashOut = () => {
    saveBest(STORAGE_KEY, score);
    setBest((b) => Math.max(b, score));
    setFinished(true);
  };

  const restart = () => {
    setScore(0);
    setFinished(false);
    loadBoard(1);
  };

  if (finished) {
    const gameUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}/games/word-bloom`
        : "https://mrbquotes.com/games/word-bloom";
    const shareText = buildShareText({
      gameName: "Word Bloom",
      scoreLine: `I reached level ${level} and scored ${score} points on the endless letter wheel. Beat that:`,
      url: gameUrl,
      extra: "🌼 Free · No signup · Unlimited levels",
    });
    return (
      <FinishScreen
        score={score}
        total={Math.max(score, 1)}
        best={best}
        onRestart={restart}
        title={`Word Bloom · Level ${level}`}
        numeral="VII"
        hubSlug="word-bloom"
        shareText={shareText}
      />
    );
  }

  const radius = live ? 132 : 104;
  const tile = live ? 64 : 52;
  const boxSize = radius * 2 + tile + 8;

  return (
    <GameShell
      title="Word Bloom"
      numeral="VII"
      score={score}
      round={level}
      hubSlug="word-bloom"
    >
      {/* Progress */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card/60 px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Level {level} · {letters.length} letters
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>
            Found <span className="font-mono text-gold">{found.size}</span> / {board.answers.length}
          </span>
          {board.pangram && (
            <span className="hidden sm:inline">
              One word uses <span className="text-gold">all {letters.length}</span> letters
            </span>
          )}
        </div>
      </div>

      {/* Answer grid */}
      <div className="mt-5 grid gap-3">
        {byLength.map(([len, wordsOfLen]) => (
          <div key={len} className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="w-8 shrink-0 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{len}·</span>
            {wordsOfLen.map((w) => {
              const isFound = found.has(w);
              const show = isFound ? w.length : revealed[w] ?? 0;
              return (
                <div key={w} className="flex gap-1">
                  {w.split("").map((ch, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-center rounded-md border font-display uppercase ${
                        live ? "h-10 w-10 text-xl" : "h-8 w-8 text-base"
                      } ${
                        isFound
                          ? "border-success bg-success/15 text-success"
                          : i < show
                            ? "border-gold/50 bg-gold/10 text-gold"
                            : "border-border bg-card/40 text-transparent"
                      }`}
                    >
                      {i < show ? ch : "·"}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bonus words — real words spelled from the wheel that are not on the grid */}
      {bonus.size > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground">Bonus</span>
          {[...bonus].sort().map((w) => (
            <span
              key={w}
              className="rounded-full border border-success/40 bg-success/10 px-2.5 py-0.5 font-mono text-xs uppercase text-success"
            >
              {w}
            </span>
          ))}
        </div>
      )}

      {/* Current word */}
      <div className="mt-6 flex min-h-[3.25rem] items-center justify-center">
        {current ? (
          <div className="flex gap-1.5">
            {current.split("").map((ch, i) => (
              <span
                key={i}
                className={`flex items-center justify-center rounded-md border border-gold/60 bg-gold/10 font-display uppercase text-gold ${
                  live ? "h-12 w-12 text-2xl" : "h-10 w-10 text-xl"
                }`}
              >
                {ch}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Tap letters to spell a word · Enter to submit</span>
        )}
      </div>

      {/* Flash message */}
      <div className="mt-2 flex h-6 items-center justify-center text-sm">
        {flash && (
          <span
            className={
              flash.kind === "hit" ? "text-success" : flash.kind === "dupe" ? "text-gold" : "text-error"
            }
          >
            {flash.text}
          </span>
        )}
      </div>

      {/* Letter wheel */}
      <div className="mt-2 flex justify-center">
        <div className="relative" style={{ width: boxSize, height: boxSize }}>
          <div className="absolute inset-4 rounded-full border border-border/70 bg-card/40" />
          {letters.map((ch, i) => {
            const angle = (i / letters.length) * Math.PI * 2 - Math.PI / 2;
            const x = boxSize / 2 + radius * Math.cos(angle) - tile / 2;
            const y = boxSize / 2 + radius * Math.sin(angle) - tile / 2;
            const used = picked.includes(i);
            return (
              <button
                key={`${ch}-${i}`}
                type="button"
                onClick={() => pressLetter(i)}
                style={{ left: x, top: y, width: tile, height: tile }}
                className={`absolute flex items-center justify-center rounded-full border font-display uppercase transition ${
                  live ? "text-3xl" : "text-2xl"
                } ${
                  used
                    ? "border-gold bg-gold/20 text-gold/50"
                    : "border-gold/40 bg-card text-foreground hover:border-gold hover:bg-gold/10"
                }`}
              >
                {ch}
              </button>
            );
          })}
        </div>
      </div>

      {/* Controls */}
      {!levelDone && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button onClick={submit} className="rounded-full bg-gradient-gold px-6 py-2.5 text-sm font-semibold text-ink">
            Submit
          </button>
          <button onClick={() => setPicked((p) => p.slice(0, -1))} className="rounded-full border border-border px-5 py-2.5 text-sm" aria-label="Delete last letter">
            ⌫
          </button>
          <button onClick={() => setPicked([])} className="rounded-full border border-border px-5 py-2.5 text-sm">
            Clear
          </button>
          <button onClick={shuffleWheel} className="rounded-full border border-border px-5 py-2.5 text-sm">
            ⤮ Shuffle
          </button>
          <button onClick={useHint} className="rounded-full border border-gold/50 px-5 py-2.5 text-sm text-gold">
            Hint −{HINT_COST}
          </button>
          <button onClick={cashOut} className="rounded-full border border-border px-5 py-2.5 text-sm text-muted-foreground">
            Finish &amp; share
          </button>
        </div>
      )}

      {/* Level complete */}
      {levelDone && (
        <div className="mt-6 rounded-2xl border border-gold/40 bg-gold/5 p-6 text-center animate-reveal">
          <div className="flex justify-center gap-1 text-2xl">
            {[1, 2, 3].map((s) => (
              <span key={s} className={s <= stars ? "text-gold" : "text-border"}>★</span>
            ))}
          </div>
          <div className="mt-2 font-display text-2xl text-gold">Level {level} bloomed</div>
          <p className="mt-1 text-sm text-muted-foreground">
            All {board.answers.length} words found{bonus.size ? ` + ${bonus.size} bonus word${bonus.size > 1 ? "s" : ""}` : ""}{hintsUsed ? ` · ${hintsUsed} hint${hintsUsed > 1 ? "s" : ""} used` : " · no hints"}
          </p>

          {/* Quote reward — the Mr. B twist */}
          <div className="mx-auto mt-5 max-w-xl rounded-xl border border-border bg-card p-5 grain">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Your reward · a quote featuring “{board.reward.word}”
            </div>
            <p className="mt-2 font-display text-lg sm:text-xl leading-snug">{board.reward.quote.text}</p>
            <div className="mt-2 text-sm text-gold">
              — {board.reward.quote.author}
              {board.reward.quote.era ? <span className="text-muted-foreground"> · {board.reward.quote.era}</span> : null}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button onClick={nextLevel} className="rounded-full bg-gradient-gold px-6 py-2.5 text-sm font-semibold text-ink">
              Level {level + 1} →
            </button>
            <button onClick={cashOut} className="rounded-full border border-gold/50 px-6 py-2.5 text-sm font-semibold text-gold">
              Finish &amp; share run
            </button>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Levels never run out — {TOTAL_BOARDS} hand-checked letter sets, reshuffled endlessly.
          </p>
        </div>
      )}
    </GameShell>
  );
}
